/**
 * Step 6 — Risk priority × value heat map (Story 15.B port from
 * legacy `Step6RiskAssessment.jsx` lines 168-298).
 *
 * 4×5 grid (Treatment Priority rows × Risk Value cols, 5..1). Each
 * cell shows the count of matching risks plus the first 2 risk IDs.
 * Hover overflow (full ID list) uses the native `title` attribute —
 * v2's static-only motion rule rules out the legacy bespoke popover.
 *
 * Cell click → fires `onCellSelect({ priority, value })`. The
 * dispatcher (Step6RiskAssessment) uses this to narrow the table
 * above to just the cell's risks. Click the same cell again to
 * clear.
 *
 * Cell coloring: `getRiskValueColor(value, { alpha: 0.3 })` for
 * background, `alpha: 0.6` for border. Uses the canonical
 * risk-value palette.
 *
 * Legend: 5 `LevelBadge`s — one per risk value (5→Critical,
 * 4→High, 3→Medium, 2→Low, 1→Very Low). No inline colored divs.
 */
import React, { useMemo, useState } from 'react';
import type { RiskRecord } from './riskFieldSchema';
import { getRiskValueColor } from '../../../../utils/severityStyle';
import LevelBadge from '../../../../components/ui/LevelBadge';
import { Icon } from '../../../../components/ui/Icon';
import styles from './RiskMatrix.module.css';

const PRIORITIES = ['Immediate', 'High', 'Medium', 'Low'] as const;
type Priority = (typeof PRIORITIES)[number];

const RISK_VALUES = [5, 4, 3, 2, 1] as const;
type RiskValue = (typeof RISK_VALUES)[number];

const VALUE_LABELS: Record<RiskValue, string> = {
  5: 'Critical',
  4: 'High',
  3: 'Medium',
  2: 'Low',
  1: 'Very Low',
};

type MatrixGrid = Record<Priority, Record<RiskValue, RiskRecord[]>>;

export interface MatrixFilter {
  priority: Priority;
  value: RiskValue;
}

function emptyGrid(): MatrixGrid {
  const grid = {} as MatrixGrid;
  for (const p of PRIORITIES) {
    grid[p] = {} as Record<RiskValue, RiskRecord[]>;
    for (const v of RISK_VALUES) grid[p][v] = [];
  }
  return grid;
}

function buildGrid(risks: RiskRecord[]): MatrixGrid {
  const grid = emptyGrid();
  for (const r of risks) {
    const rawPriority = (r as any).treatment_priority;
    const priority: Priority = (PRIORITIES as readonly string[]).includes(rawPriority)
      ? (rawPriority as Priority)
      : 'Low';
    const rawValue = parseInt(String((r as any).risk_value ?? ''), 10);
    if (Number.isFinite(rawValue) && (RISK_VALUES as readonly number[]).includes(rawValue)) {
      grid[priority][rawValue as RiskValue].push(r);
    }
  }
  return grid;
}

export interface RiskMatrixProps {
  risks: RiskRecord[];
  /** Currently-selected cell (highlighted + filter banner). */
  filter: MatrixFilter | null;
  /** Click handler — null clears, otherwise sets the filter. Toggling
   *  the same cell off is handled by the dispatcher. */
  onCellSelect: (filter: MatrixFilter | null) => void;
}

export default function RiskMatrix({ risks, filter, onCellSelect }: RiskMatrixProps) {
  // Collapsed by default; state is not persisted, so it resets on every
  // remount of this step.
  const [collapsed, setCollapsed] = useState<boolean>(true);

  const toggle = () => setCollapsed((prev) => !prev);

  const grid = useMemo(() => buildGrid(risks), [risks]);

  const handleCellClick = (priority: Priority, value: RiskValue, hasRisks: boolean) => {
    if (!hasRisks) return;
    const isSelected = filter && filter.priority === priority && filter.value === value;
    onCellSelect(isSelected ? null : { priority, value });
  };

  return (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.head}
        onClick={toggle}
        aria-expanded={!collapsed}
      >
        <span className={styles.headLeft}>
          <span className={styles.title}>Risk Heat Map</span>
          <span className={styles.subtitle}>· Priority × Risk Value · {risks.length} total</span>
        </span>
        <span className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}>
          <Icon name="chevron-down" size={14} />
        </span>
      </button>

      {!collapsed && (
        <div className={styles.body}>
          {filter && (
            <div className={styles.filterBanner}>
              <span>
                Filtered to <strong>{filter.priority}</strong> priority × value{' '}
                <strong>{filter.value}</strong> ({VALUE_LABELS[filter.value]}) —{' '}
                {grid[filter.priority][filter.value].length} risk
                {grid[filter.priority][filter.value].length === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                className={styles.filterClear}
                onClick={() => onCellSelect(null)}
              >
                Clear filter ✕
              </button>
            </div>
          )}

          <div className={styles.gridWrapper}>
            <div className={styles.grid}>
              <div className={styles.corner}>Priority / Value</div>
              {RISK_VALUES.map((v) => (
                <div key={`h-${v}`} className={styles.columnHeader}>
                  <span className={styles.headerValue}>{v}</span>
                  <span className={styles.headerLabel}>{VALUE_LABELS[v]}</span>
                </div>
              ))}

              {PRIORITIES.map((priority) => (
                <React.Fragment key={priority}>
                  <div className={styles.rowHeader}>{priority}</div>
                  {RISK_VALUES.map((value) => {
                    const cellRisks = grid[priority][value];
                    const hasRisks = cellRisks.length > 0;
                    const isSelected =
                      !!filter && filter.priority === priority && filter.value === value;

                    const cellClasses = [
                      styles.cell,
                      hasRisks ? styles.cellInteractive : '',
                      isSelected ? styles.cellSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ');

                    const bg = hasRisks
                      ? getRiskValueColor(value, { alpha: 0.3 }) ?? undefined
                      : undefined;
                    const border = hasRisks
                      ? getRiskValueColor(value, { alpha: 0.6 }) ?? undefined
                      : undefined;

                    const idList = cellRisks.map((r) => r.risk_id ?? '').filter(Boolean);
                    const titleText = hasRisks
                      ? `${cellRisks.length} risk${cellRisks.length === 1 ? '' : 's'}: ${idList.join(', ')}`
                      : '';

                    return (
                      <div
                        key={`${priority}-${value}`}
                        className={cellClasses}
                        style={{
                          ...(bg ? { background: bg } : {}),
                          ...(border ? { borderColor: border } : {}),
                        }}
                        title={titleText}
                        onClick={() => handleCellClick(priority, value, hasRisks)}
                        role={hasRisks ? 'button' : undefined}
                        aria-pressed={isSelected || undefined}
                      >
                        {hasRisks ? (
                          <>
                            <span className={styles.cellCount}>{cellRisks.length}</span>
                            <span className={styles.cellIds}>
                              {idList.slice(0, 2).join(', ')}
                              {idList.length > 2 ? '…' : ''}
                            </span>
                          </>
                        ) : (
                          <span className={styles.cellEmpty}>—</span>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={styles.legend}>
            <span className={styles.legendTitle}>Risk Value</span>
            {RISK_VALUES.map((v) => (
              <LevelBadge
                key={v}
                color={getRiskValueColor(v) ?? 'var(--color-status-neutral)'}
                label={`${v} · ${VALUE_LABELS[v]}`}
                size="sm"
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
